/**
 * Pure booking-draft helpers shared by the desktop booking form
 * (MultiServiceAppointmentForm) and the mobile new-appointment / rebook
 * screens. No React. Rows are salon wall-clock (`date` "YYYY-MM-DD" +
 * `time` "HH:mm"); API datetimes go through parseApiDateTime elsewhere.
 */
import moment from "moment-timezone";
import { parseApiDateTime } from "../../../../utils/timeUtils";
import type { SalonService } from "../../../../types/salon";
import type { ServiceAdjustmentForm } from "../../../../types/serviceAdjustmentForms";
import {
  BookingMode,
  ServiceRow,
  emptyRow,
  staffServiceFields,
  staffServiceFor,
} from "./bookingTypes";
import { canDeleteServiceRow } from "./guards";
import { resolveStaffServiceId, rowStartIso } from "./rescheduleCommit";
import type { SuggestedService, SuggestedSlot } from "./useSuggestedTimes";

/**
 * The salon services, each with its `servicing_staff` (who performs it, with
 * their price / durations and staff_service id). The one source for every
 * staff ↔ service question in the booking flows (see staffServiceFor).
 */
type Services = SalonService[] | null | undefined;

const serviceIn = (
  services: Services,
  salonServiceId: string,
): SalonService | undefined =>
  salonServiceId
    ? (services || []).find((s) => s.id === salonServiceId)
    : undefined;

/**
 * Note shown under the review-step subtotal (desktop + mobile). The review
 * deliberately shows no tax: sales tax is computed server-side per line item
 * from each service's linked tax rates (`salon_service_tax_rates`), and many
 * services have none, so any client-side estimate here can disagree with what
 * checkout actually charges.
 */
export const REVIEW_TAX_NOTE = "Tax, if applicable, is calculated at checkout";

// ── Time ─────────────────────────────────────────────────────────────────

export const TIME_STEP = 15;

export const toMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const fromMinutes = (mins: number): string => {
  const wrapped = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** "HH:mm" → "h:mm A" ("" for no time). */
export const fmtTime = (hhmm: string): string =>
  hhmm ? moment(hhmm, "HH:mm").format("h:mm A") : "";

/**
 * A row's base minutes as scheduled: its unadjusted base plus the selected
 * adjustment options' minutes. Display / geometry only; the payload sends
 * `baseDuration` (the backend adds the option deltas itself).
 */
export const adjustedBaseDuration = (r: ServiceRow): number =>
  Math.max(0, (r.baseDuration || 0) + (r.adjustmentDuration || 0));

/**
 * Scheduled length (adjusted base + processing + finishing); buffer is
 * padding.
 */
export const serviceLength = (r: ServiceRow): number =>
  adjustedBaseDuration(r) + r.processingTime + r.finishingTime;

/**
 * Minutes from one service's start to the next back-to-back service's start:
 * the full occupied span including buffer. Initial default of an added row.
 */
export const serviceStride = (r: ServiceRow): number =>
  serviceLength(r) + r.bufferTime;

/** A row's end time "HH:mm" ("" without a start time). */
export const rowEndTime = (r: ServiceRow): string =>
  r.time ? fromMinutes(toMinutes(r.time) + serviceLength(r)) : "";

// ── Staff ↔ service (SalonService.servicing_staff) ──────────────────────

/** Services a staff member can perform. */
export const servicesForStaff = (
  services: Services,
  employeeId: string,
): Set<string> =>
  new Set(
    (services || [])
      .filter((s) => !!staffServiceFor(s, employeeId))
      .map((s) => s.id),
  );

/** Staff who can perform a service. */
export const staffIdsForService = (
  services: Services,
  salonServiceId: string,
): Set<string> =>
  new Set(
    (serviceIn(services, salonServiceId)?.servicing_staff || []).map(
      (ss) => ss.staff_id,
    ),
  );

/**
 * Services at least one bookable staff member can perform. `null` while
 * services haven't loaded (don't restrict yet).
 */
export const performableServiceIds = (
  services: Services,
  bookableStaffIds: string[],
): Set<string> | null => {
  if (!services) return null;
  const bookable = new Set(bookableStaffIds);
  return new Set(
    services
      .filter((s) =>
        (s.servicing_staff || []).some((ss) => bookable.has(ss.staff_id)),
      )
      .map((s) => s.id),
  );
};

/**
 * Service ids offered for a row: a concrete provider restricts to what they
 * can do; no provider restricts to services SOME bookable staff can perform.
 */
export const serviceIdsForRow = (
  row: Pick<ServiceRow, "employeeId">,
  allServiceIds: string[],
  services: Services,
  performable: Set<string> | null,
): string[] => {
  if (!row.employeeId) {
    return performable
      ? allServiceIds.filter((id) => performable.has(id))
      : allServiceIds;
  }
  const allowed = servicesForStaff(services, row.employeeId);
  return allServiceIds.filter((id) => allowed.has(id));
};

/**
 * Staff ids offered for a row: the providers of the
 * chosen service, or every bookable staff member before a service is chosen.
 */
export const staffIdsForRow = (
  row: Pick<ServiceRow, "salonServiceId">,
  bookableStaffIds: string[],
  services: Services,
): string[] => {
  if (!row.salonServiceId) return bookableStaffIds;
  const ids = staffIdsForService(services, row.salonServiceId);
  return bookableStaffIds.filter((id) => ids.has(id));
};

/**
 * What a service costs and how long it takes with a given provider (their
 * servicing_staff overrides, else the salon defaults): the picker row
 * detail. `minutes` is the scheduled length (base + processing + finishing).
 */
export const serviceQuote = (
  svc: SalonService | undefined,
  staffId: string,
): { price: number; minutes: number } => {
  const f = staffServiceFields(svc, staffId);
  return {
    price: f.price,
    minutes: f.baseDuration + f.processingTime + f.finishingTime,
  };
};

// ── Row edits ────────────────────────────────────────────────────────────

/**
 * Patch one row from ITS latest value. Single-day rule: a date change
 * applies to every row.
 */
export const patchRowIn = (
  rows: ServiceRow[],
  uid: number,
  patchFor: (row: ServiceRow) => Partial<ServiceRow>,
): ServiceRow[] => {
  const target = rows.find((r) => r.uid === uid);
  if (!target) return rows;
  const patch = patchFor(target);
  return rows.map((r) => {
    if (r.uid === uid) return { ...r, ...patch };
    if ("date" in patch) return { ...r, date: patch.date ?? null };
    return r;
  });
};

/**
 * Choosing a service: keep a provider who can perform it, adopt that
 * provider's price / durations (their servicing_staff entry), reset
 * adjustments. Clearing it (null / "") empties only the service: the
 * provider stays and the row falls back to the blank-row defaults.
 */
export const selectServicePatch = (
  row: ServiceRow,
  salonServiceId: string | null,
  svc: SalonService | undefined,
): Partial<ServiceRow> => {
  if (!salonServiceId) {
    return {
      salonServiceId: "",
      ...staffServiceFields(undefined, ""),
      adjustmentOptionIds: [],
      adjustmentDuration: 0,
    };
  }
  const keepStaff = staffServiceFor(svc, row.employeeId) ? row.employeeId : "";
  return {
    salonServiceId,
    employeeId: keepStaff,
    ...staffServiceFields(svc, keepStaff),
    adjustmentOptionIds: [],
    adjustmentDuration: 0,
  };
};

/**
 * Choosing a provider: one who can't perform the row's service clears the
 * service (and its adjustment answers); otherwise the row adopts their
 * price / durations and keeps its adjustment answers (their minutes are
 * separate from the base, so they survive). Clearing it ("") keeps the
 * service at the salon defaults.
 */
export const selectStaffPatch = (
  row: ServiceRow,
  employeeId: string,
  svc: SalonService | undefined,
): Partial<ServiceRow> => {
  if (employeeId && row.salonServiceId && !staffServiceFor(svc, employeeId)) {
    return {
      employeeId,
      salonServiceId: "",
      adjustmentOptionIds: [],
      adjustmentDuration: 0,
    };
  }
  return row.salonServiceId
    ? { employeeId, ...staffServiceFields(svc, employeeId) }
    : { employeeId };
};

/**
 * Append a row: it inherits the booking day and, as an initial default only,
 * starts after the previous row (its start + full stride).
 */
export const appendRow = (
  rows: ServiceRow[],
  uid: number,
  fallbackDate: string | null,
): ServiceRow[] => {
  const prevRow = rows.length ? rows[rows.length - 1] : null;
  const row = emptyRow(uid, prevRow ? prevRow.date : fallbackDate);
  if (prevRow && prevRow.time) {
    row.time = fromMinutes(toMinutes(prevRow.time) + serviceStride(prevRow));
  }
  return [...rows, row];
};

/**
 * Lay every row out back to back from `startTime`, in row order: the first
 * row starts there and each next row starts where the previous one's full
 * span (serviceStride) ends. Rows are not locked afterwards: a drag or an
 * inline time edit moves one row on its own.
 */
export const layBackToBack = (
  rows: ServiceRow[],
  startTime: string,
): ServiceRow[] => {
  let cursor = toMinutes(startTime);
  return rows.map((r) => {
    const placed = { ...r, time: fromMinutes(cursor) };
    cursor += serviceStride(r);
    return placed;
  });
};

/** Remove a row (the first service is never removable: guard). */
export const removeRowFrom = (rows: ServiceRow[], uid: number): ServiceRow[] =>
  canDeleteServiceRow(rows, uid) ? rows.filter((r) => r.uid !== uid) : rows;

/** Next unused row id. */
export const nextRowUid = (rows: ServiceRow[]): number =>
  rows.reduce((max, r) => Math.max(max, r.uid), 0) + 1;

/**
 * What GET /suggested-times is asked for: every chosen service in row order
 * (`serviceIds`), each row's provider aligned 1:1 (`staffIds`, "" = any
 * qualified provider), the booking day, and the first row's time as the
 * requested start (`startTime`, salon-local "HH:mm"; the endpoint returns it
 * first when it fits, then the closest alternatives). `rowUids` maps the
 * response's per-service entries (same order) back to rows.
 */
export interface SuggestionQuery {
  serviceIds: string[];
  staffIds: string[];
  startTime?: string;
  date: string | null;
  rowUids: number[];
}

export const suggestionQuery = (rows: ServiceRow[]): SuggestionQuery => {
  const chosen = rows.filter((r) => r.salonServiceId);
  return {
    serviceIds: chosen.map((r) => r.salonServiceId),
    staffIds: chosen.map((r) => r.employeeId || ""),
    startTime: chosen[0]?.time || undefined,
    date: chosen[0]?.date ?? null,
    rowUids: chosen.map((r) => r.uid),
  };
};

/**
 * Apply a suggested slot: each queried row takes its own service's start
 * (`slot.services[i]`, UTC → salon wall-clock) and, when it has no provider
 * yet, that service's provider (adopting their price / durations when the
 * salon service is known). A row with a provider keeps it (the endpoint
 * honors `staff_ids`). Every row adopts the slot's day (single-day rule).
 */
export const applySuggestedSlot = (
  rows: ServiceRow[],
  query: Pick<SuggestionQuery, "rowUids">,
  slot: SuggestedSlot,
  timezone: string,
  services: Services,
): ServiceRow[] => {
  if (query.rowUids.length === 0) return rows;
  const day = parseApiDateTime(slot.start_dt, timezone).format("YYYY-MM-DD");
  const byUid = new Map<number, SuggestedService>();
  query.rowUids.forEach((uid, i) => {
    const svc = slot.services?.[i];
    if (svc) byUid.set(uid, svc);
  });
  return rows.map((r) => {
    const svc = byUid.get(r.uid);
    if (!svc) return { ...r, date: day };
    const time = parseApiDateTime(svc.start_dt, timezone).format("HH:mm");
    const salonSvc = serviceIn(services, r.salonServiceId);
    const staffId = r.employeeId ? "" : suggestedStaffId(salonSvc, svc);
    if (!staffId) return { ...r, date: day, time };
    return {
      ...r,
      date: day,
      time,
      employeeId: staffId,
      ...(salonSvc ? staffServiceFields(salonSvc, staffId) : {}),
    };
  });
};

/**
 * Whether a suggested slot is the one the draft currently uses: its start is
 * the first chosen row's day + time (the same rule as the mobile chips).
 * Derived, never stored: a manual time edit or slot click clears it.
 */
export const isSuggestionSelected = (
  rows: ServiceRow[],
  slot: SuggestedSlot,
  timezone: string,
): boolean => {
  const lead = rows.find((r) => r.salonServiceId);
  if (!lead || !lead.date || !lead.time) return false;
  const start = parseApiDateTime(slot.start_dt, timezone);
  return (
    start.format("YYYY-MM-DD") === lead.date &&
    start.format("HH:mm") === lead.time
  );
};

/**
 * Provider of a suggested service: its staff_id, else the staff of the
 * salon service's servicing_staff entry with its staff_service_id.
 */
export const suggestedStaffId = (
  salonSvc: SalonService | undefined,
  svc: Pick<SuggestedService, "staff_id" | "staff_service_id">,
): string =>
  svc.staff_id ||
  (svc.staff_service_id
    ? (salonSvc?.servicing_staff || []).find(
        (ss) => ss.id === svc.staff_service_id,
      )?.staff_id
    : undefined) ||
  "";

// ── Service adjustments ──────────────────────────────────────────────────

/**
 * Active adjustment forms for a service: junction-matched, is_active, sorted
 * by priority with questions / options likewise sorted.
 */
export const activeAdjustmentForms = (
  adjustmentForms: ServiceAdjustmentForm[] | null | undefined,
  formJunctions:
    | { salon_service_id: string; service_adjustment_form_id: string }[]
    | null
    | undefined,
  salonServiceId: string,
): ServiceAdjustmentForm[] => {
  if (!salonServiceId || !adjustmentForms || !formJunctions) return [];
  const formIds = new Set(
    formJunctions
      .filter((j) => j.salon_service_id === salonServiceId)
      .map((j) => j.service_adjustment_form_id),
  );
  if (formIds.size === 0) return [];
  return adjustmentForms
    .filter(
      (form) =>
        form.is_active &&
        !!form.service_adjustment_form_id &&
        formIds.has(form.service_adjustment_form_id),
    )
    .sort((a, b) => a.priority - b.priority)
    .map((form) => ({
      ...form,
      questions: form.questions
        .slice()
        .sort((a, b) => a.priority - b.priority)
        .map((q) => ({
          ...q,
          options: q.options.slice().sort((a, b) => a.priority - b.priority),
        })),
    }));
};

/** Every option of the forms a row's selected ids point at. */
const selectedOptions = (
  row: Pick<ServiceRow, "adjustmentOptionIds">,
  forms: ServiceAdjustmentForm[],
): ServiceAdjustmentForm["questions"][number]["options"] => {
  const ids = row.adjustmentOptionIds;
  if (!ids || ids.length === 0) return [];
  return forms.flatMap((form) =>
    form.questions.flatMap((q) =>
      q.options.filter((o) => !!o.option_id && ids.includes(o.option_id)),
    ),
  );
};

/**
 * Duration delta (minutes) of a row's selected adjustment options. Display /
 * geometry only: the backend adds each option's delta from its id, on top
 * of the unadjusted base_duration_mins.
 */
export const adjustmentDurationDelta = (
  row: Pick<ServiceRow, "adjustmentOptionIds">,
  forms: ServiceAdjustmentForm[],
): number =>
  selectedOptions(row, forms).reduce(
    (sum, o) => sum + (o.duration_delta_mins || 0),
    0,
  );

/**
 * Single-select per question. Neither delta is applied to the row's own
 * values: `baseDuration` and `price` stay unadjusted and the backend adds
 * both deltas as a sub line item per option id. The row's
 * `adjustmentDuration` is re-derived from the selected ids for display /
 * geometry (see adjustmentPriceDelta for the price).
 */
export const toggleAdjustmentOption = (
  row: ServiceRow,
  forms: ServiceAdjustmentForm[],
  optionId: string,
  questionId: string,
): ServiceRow => {
  const optionIds = forms
    .flatMap((form) => form.questions)
    .filter((q) => q.question_id === questionId)
    .flatMap((q) => q.options.map((o) => o.option_id))
    .filter((id): id is string => !!id);
  const kept = (row.adjustmentOptionIds || []).filter(
    (id) => !optionIds.includes(id),
  );
  const adjustmentOptionIds = optionId ? [...kept, optionId] : kept;
  return {
    ...row,
    adjustmentOptionIds,
    adjustmentDuration: adjustmentDurationDelta({ adjustmentOptionIds }, forms),
  };
};

/** Price delta of a row's selected adjustment options (display only). */
export const adjustmentPriceDelta = (
  row: ServiceRow,
  forms: ServiceAdjustmentForm[],
): number =>
  selectedOptions(row, forms).reduce((sum, o) => sum + (o.price_delta || 0), 0);

// ── Validity + write ─────────────────────────────────────────────────────

/** The staff_service_id to book with (the row's provider + service). */
export const finalStaffServiceId = (
  services: Services,
  r: ServiceRow,
): string | undefined => resolveStaffServiceId(services, r);

/** The field a draft is still missing before it can advance. */
export type DraftBlockerField =
  | "client"
  | "service"
  | "provider"
  | "date"
  | "time";

/**
 * Why a draft can't advance yet: the first missing piece, in on-screen order
 * (client, then each row's service → provider → date → time). `uid` is the
 * offending row (null for the client). `message` is user-facing.
 */
export interface DraftBlocker {
  uid: number | null;
  field: DraftBlockerField;
  message: string;
}

export const draftBlocker = (
  rows: ServiceRow[],
  clientUuid: string,
  services: Services,
): DraftBlocker | null => {
  if (!clientUuid) {
    return {
      uid: null,
      field: "client",
      message: "Select a client to continue.",
    };
  }
  if (rows.length === 0) {
    return {
      uid: null,
      field: "service",
      message: "Add a service to continue.",
    };
  }
  const multi = rows.length > 1;
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const svcName = (services || []).find(
      (s) => s.id === r.salonServiceId,
    )?.name;
    // "Facial" / "Facial (service 2)"; unnamed rows fall back to "service 2".
    const ordinal = `service ${i + 1}`;
    const label = svcName
      ? multi
        ? `${svcName} (${ordinal})`
        : svcName
      : ordinal;
    if (!r.salonServiceId) {
      return {
        uid: r.uid,
        field: "service",
        message: multi
          ? `Select a service for ${ordinal}.`
          : "Select a service to continue.",
      };
    }
    if (!r.employeeId) {
      return {
        uid: r.uid,
        field: "provider",
        message: `Select a provider for ${label}.`,
      };
    }
    if (!resolveStaffServiceId(services, r)) {
      return {
        uid: r.uid,
        field: "provider",
        message: `The selected provider doesn't perform ${label}. Choose another provider.`,
      };
    }
    if (!r.date) {
      return {
        uid: r.uid,
        field: "date",
        message: `Select a date for ${label}.`,
      };
    }
    if (!r.time) {
      return {
        uid: r.uid,
        field: "time",
        message: `Select a start time for ${label}.`,
      };
    }
  }
  return null;
};

/**
 * Every row has a service, a qualified provider, a date and a time, and a
 * client is chosen. Past dates are allowed (staff book, rebook and
 * reschedule into the past, e.g. to record a walk-in after the fact).
 */
export const isDraftBookable = (
  rows: ServiceRow[],
  clientUuid: string,
  services: Services,
): boolean => draftBlocker(rows, clientUuid, services) === null;

/**
 * POST /appointments body for create / rebook: exactly what the rows show.
 * Each service carries its row's provider (staff_service_id), durations and
 * base price (`unit_price`; without it the backend would price from the
 * staff_services row, not the edited price on screen). Rebook also carries
 * the source transaction uuid. `base_duration_mins` and `unit_price` are
 * the UNADJUSTED values: the backend adds each adjustment option's duration
 * and price delta from the option ids (sending an adjusted base would count
 * the minutes twice).
 */
export const buildBookingPayload = ({
  rows,
  clientUuid,
  mode,
  sourceTransactionId,
  timezone,
  services,
}: {
  rows: ServiceRow[];
  clientUuid: string;
  mode: Exclude<BookingMode, "edit">;
  sourceTransactionId?: string;
  timezone: string;
  services: Services;
}): Record<string, unknown> => {
  const bookedServices = rows.map((r) => ({
    staff_service_id: finalStaffServiceId(services, r),
    start_dt: rowStartIso(r, timezone),
    base_duration_mins: r.baseDuration,
    processing_time_mins: r.processingTime,
    finishing_time_mins: r.finishingTime,
    buffer_time_mins: r.bufferTime,
    ...(r.adjustmentOptionIds && r.adjustmentOptionIds.length > 0
      ? { service_adjustment_form_option_ids: r.adjustmentOptionIds }
      : {}),
    unit_price: r.price,
  }));
  const payload: Record<string, unknown> = {
    booked_services: bookedServices,
    source: mode === "rebook" ? "AP - Staff Rebooking" : "Manually input",
    client_uuid: clientUuid,
  };
  if (mode === "rebook" && sourceTransactionId) {
    payload.rebooking_source_uuid = sourceTransactionId;
  }
  return payload;
};

/**
 * Place rows at a new first-service start, keeping each row's offset from
 * the first (rebook moves the whole appointment together).
 */
export const rowsAtStart = (
  rows: ServiceRow[],
  offsets: number[],
  time: string,
): ServiceRow[] =>
  rows.map((r, i) => ({
    ...r,
    time: time ? fromMinutes(toMinutes(time) + (offsets[i] || 0)) : "",
  }));
